import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  UserPlus, ShoppingCart, HelpCircle,
  Download, Swords, Shield, Newspaper, User, Settings,
  LogIn, ChevronDown, Star, PlayCircle, LogOut, Menu, X,
} from 'lucide-react';
import axios from 'axios';

const LOGO_LOCAL    = '/logo.png';
const LOGO_FALLBACK = 'https://customer-assets.emergentagent.com/job_frostbound-wow/artifacts/lcob9xzr_frostbound_logo_final.png';
const BACKEND_URL   = process.env.REACT_APP_BACKEND_URL ?? `${window.location.protocol}//${window.location.hostname}:8001`;

const leftLinks = [
  { icon: PlayCircle,   label: 'PRIMEROS PASOS', href: '/primeros-pasos', testId: 'nav-primeros-pasos' },
  { icon: Swords,       label: 'JUGABILIDAD',     href: '/jugabilidad',    testId: 'nav-jugabilidad'    },
  { icon: Newspaper,    label: 'NOTICIAS',        href: '/noticias',       testId: 'nav-noticias'       },
  { icon: Shield,       label: 'ARMERÍA',         href: '/armeria',        testId: 'nav-armeria'        },
];

const rightLinks = [
  { icon: ShoppingCart, label: 'TIENDA',    href: '/tienda',    testId: 'nav-tienda'    },
  { icon: Download,     label: 'DESCARGAS', href: '/descargas', testId: 'nav-descargas' },
  { icon: HelpCircle,   label: 'SOPORTE',   href: '/soporte',   testId: 'nav-soporte'   },
];

const allMenuLinks = [...leftLinks, ...rightLinks];

function DropdownContent({ user, onClose, onLogout }) {
  return user ? (
    <>
      <Link to="/cuenta" className="nav-dropdown-item" onClick={onClose} data-testid="dropdown-cuenta">
        <User size={13} /><span>Mi Cuenta</span>
      </Link>
      <Link to="/votar" className="nav-dropdown-item" onClick={onClose} data-testid="dropdown-votar">
        <Star size={13} /><span>Votar</span>
      </Link>
      {user.role === 'admin' && (
        <Link to="/admin" className="nav-dropdown-item" onClick={onClose} data-testid="dropdown-admin">
          <Settings size={13} /><span>Admin</span>
        </Link>
      )}
      <div className="nav-dropdown-divider" />
      <button className="nav-dropdown-item nav-dropdown-item--btn" onClick={onLogout} data-testid="dropdown-logout">
        <LogOut size={13} /><span>Cerrar Sesión</span>
      </button>
    </>
  ) : (
    <>
      <Link to="/login" className="nav-dropdown-item" onClick={onClose} data-testid="dropdown-login">
        <LogIn size={13} /><span>Iniciar Sesión</span>
      </Link>
      <div className="nav-dropdown-divider" />
      <Link to="/registro" className="nav-dropdown-item" onClick={onClose} data-testid="dropdown-registro">
        <UserPlus size={13} /><span>Registrarse</span>
      </Link>
    </>
  );
}

export default function Navbar() {
  const location  = useLocation();
  const { pathname } = location;
  const navigate  = useNavigate();

  const [online, setOnline]             = useState(null);
  const [user, setUser]                 = useState(undefined);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);

  const dropdownPanelRef  = useRef(null);
  const mobileMenuRef     = useRef(null);

  // ── Data fetching ──────────────────────────────────────
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/status/online`).then(({ data }) => setOnline(data.online)).catch(() => {});
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null));
    const interval = setInterval(() => {
      axios.get(`${BACKEND_URL}/api/status/online`).then(({ data }) => setOnline(data.online)).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // ── Close dropdown on outside click ───────────────────
  useEffect(() => {
    const handler = (e) => {
      if (dropdownPanelRef.current?.contains(e.target)) return;
      if (e.target.closest('[data-account-btn]')) return;
      setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Close mobile menu on outside click ────────────────
  useEffect(() => {
    const handler = (e) => {
      if (mobileMenuRef.current?.contains(e.target)) return;
      if (e.target.closest('[data-hamburger-btn]')) return;
      setShowMobileMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Close everything on route change ──────────────────
  useEffect(() => {
    setShowDropdown(false);
    setShowMobileMenu(false);
  }, [pathname]);

  // ── Account dropdown opener (desktop + mobile shared) ─
  const handleAccountClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownRect(rect);
    setShowDropdown(v => !v);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (_) {}
    setUser(null);
    setShowDropdown(false);
    navigate('/');
  };

  const isAccountActive = ['/cuenta', '/login', '/registro', '/votar', '/admin'].includes(location.pathname);

  return (
    <nav className="nav-wrapper anim-fade-down" style={{ animationDelay: '0.05s' }}>

      {/* ══ DESKTOP: split pills + center logo ══ */}
      <div className="nav-split" data-testid="navbar">

        <div className="nav-pill nav-pill--left">
          {leftLinks.map(({ icon: Icon, label, href, testId }) => (
            <Link key={testId} to={href} data-testid={testId}
              className={`nav-item ${location.pathname === href ? 'nav-item--active' : ''}`}>
              <Icon size={13} className="nav-icon" />
              <span className="nav-label">{label}</span>
            </Link>
          ))}
        </div>

        <Link to="/" className="nav-logo-center" aria-label="Inicio">
          <img src={LOGO_LOCAL} alt="Frostbound" className="nav-logo-img"
            onError={(e) => { if (e.target.src !== LOGO_FALLBACK) e.target.src = LOGO_FALLBACK; }} />
          <span className="nav-version">✦ Wrath of the Lich King 3.3.5a ✦</span>
          {online !== null && online > 0 && (
            <div className="nav-online" data-testid="online-counter">
              <span className="online-dot" /><span className="online-text">{online.toLocaleString()} online</span>
            </div>
          )}
        </Link>

        <div className="nav-pill nav-pill--right">
          {rightLinks.map(({ icon: Icon, label, href, testId }) => (
            <Link key={testId} to={href} data-testid={testId}
              className={`nav-item ${location.pathname === href ? 'nav-item--active' : ''}`}>
              <Icon size={13} className="nav-icon" />
              <span className="nav-label">{label}</span>
            </Link>
          ))}

          <div className="nav-dropdown-wrap">
            <button
              className={`nav-item nav-item--btn nav-item--account ${showDropdown || isAccountActive ? 'nav-item--active' : ''}`}
              onClick={handleAccountClick}
              data-account-btn
              data-testid="nav-cuenta-dropdown-btn"
              aria-haspopup="true"
              aria-expanded={showDropdown}
              aria-label="Cuenta"
            >
              <User size={14} className="nav-icon" />
              <span className="nav-label">CUENTA</span>
              <ChevronDown size={11} className="nav-chevron"
                style={{ transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
            </button>
          </div>
        </div>
      </div>

      {/* ══ MOBILE: compact bar ══ */}
      <div className="nav-mobile-bar">
        <button
          className="nav-mob-btn"
          onClick={() => setShowMobileMenu(v => !v)}
          data-hamburger-btn
          aria-label="Menú"
        >
          {showMobileMenu ? <X size={20} /> : <Menu size={20} />}
        </button>

        <Link to="/" className="nav-mob-logo" aria-label="Inicio">
          <img src={LOGO_LOCAL} alt="Frostbound"
            onError={(e) => { if (e.target.src !== LOGO_FALLBACK) e.target.src = LOGO_FALLBACK; }} />
          {online !== null && online > 0 && (
            <div className="nav-mob-bar-online">
              <span className="online-dot" />
              <span className="online-text">{online.toLocaleString()} online</span>
            </div>
          )}
        </Link>

        <button
          className={`nav-mob-btn nav-mob-btn--account ${showDropdown || isAccountActive ? 'nav-mob-btn--active' : ''}`}
          onClick={handleAccountClick}
          data-account-btn
          aria-label="Cuenta"
        >
          <User size={20} />
        </button>
      </div>

      {/* ══ MOBILE: slide-in menu (portal) ══ */}
      {showMobileMenu && createPortal(
        <div role="presentation" className="nav-mob-backdrop" onClick={() => setShowMobileMenu(false)} onKeyDown={() => {}}>
          <div className="nav-mob-menu" ref={mobileMenuRef} onClick={e => e.stopPropagation()}>
            <div className="nav-mob-menu-header">
              <img src={LOGO_LOCAL} alt="Frostbound" className="nav-mob-menu-logo"
                onError={(e) => { if (e.target.src !== LOGO_FALLBACK) e.target.src = LOGO_FALLBACK; }} />
              <button className="nav-mob-close" onClick={() => setShowMobileMenu(false)} aria-label="Cerrar menú">
                <X size={18} />
              </button>
            </div>
            <div className="nav-mob-menu-links">
              {allMenuLinks.map(({ icon: Icon, label, href, testId }) => (
                <Link key={testId} to={href} data-testid={`mob-${testId}`}
                  className={`nav-mob-item ${location.pathname === href ? 'nav-mob-item--active' : ''}`}
                  onClick={() => setShowMobileMenu(false)}>
                  <Icon size={18} className="nav-mob-item-icon" />
                  <span>{label}</span>
                </Link>
              ))}
            </div>
            {online !== null && online > 0 && (
              <div className="nav-mob-online">
                <span className="online-dot" />
                <span className="online-text">{online.toLocaleString()} online</span>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* ══ SHARED: account dropdown portal ══ */}
      {showDropdown && dropdownRect && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'none' }}>
          <div ref={dropdownPanelRef} className="nav-dropdown" data-testid="cuenta-dropdown"
            style={{
              position: 'absolute',
              top: dropdownRect.bottom + 10,
              right: window.innerWidth - dropdownRect.right,
              pointerEvents: 'auto',
            }}>
            <DropdownContent user={user} onClose={() => setShowDropdown(false)} onLogout={handleLogout} />
          </div>
        </div>,
        document.body
      )}
    </nav>
  );
}
